import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { PanelRecordingPlayer } from '@/components/PanelRecordingPlayer';
import { PanelTranscriptCard } from '@/components/PanelTranscriptCard';
import {
  Calendar, Plus, RefreshCw, Sparkles, Trash2, ExternalLink, Video, Mail,
  ClipboardList, Star, AlertTriangle, FileText, Loader2, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { toastEmailDispatch } from '@/lib/emailDispatchToast';
import { toastMutationError } from '@/lib/resumeEnrichmentErrors';

import { v1Get, v1Patch, v1Post } from '@/lib/api';
import { GENERIC_EVAL_FAILURE_MSG, isEvalUiStale } from '@/lib/evalErrors';
import {
  normalizePanelDimensions,
  normalizePanelStringList,
  resolvePanelEvalUiState,
} from '@/lib/panelEval';
import { formatDateRange } from '@/lib/panelInterviewForm';
import { STATUS_BADGE } from '@/components/panelInterviews/panelInterviewConstants';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const PANEL_REC_LABEL = {
  strong_hire: 'Strong Recommend',
  hire: 'Recommend',
  lean_hire: 'Recommend',
  no_hire: 'Not recommended',
  strong_no_hire: 'Not recommended',
};

function panelRecommendationLabel(rec) {
  if (!rec) return 'Pending';
  const key = String(rec).trim().toLowerCase().replace(/ /g, '_');
  return PANEL_REC_LABEL[key] || String(rec).replace(/_/g, ' ');
}

const PANEL_EVAL_IN_PROGRESS_DETAIL = 'evaluation already in progress';

function isPanelEvalAlreadyInProgressError(err) {
  const detail = err?.response?.data?.detail;
  const msg = typeof detail === 'string' ? detail : detail?.message || '';
  return err?.response?.status === 409 && msg.toLowerCase().includes(PANEL_EVAL_IN_PROGRESS_DETAIL);
}

function PanelRecordingWidget({ panelId, artifact }) {
  return (
    <div className="rounded-xl border bg-card p-4" data-testid={`panel-recording-${artifact.id}`}>
      <div className="mb-3 flex items-center gap-2">
        <Video className="h-4 w-4 text-primary" />
        <p className="text-sm font-semibold">Recording</p>
        <Badge variant="outline" className="ml-auto text-xs">{artifact.state}</Badge>
      </div>
      <PanelRecordingPlayer
        panelId={panelId}
        artifactId={artifact.id}
        testId={`panel-recording-player-${artifact.id}`}
      />
    </div>
  );
}

function PanelTranscriptPane({
  panelId,
  candidateId,
  awaitingTranscript = false,
  lastError = null,
  canEditEvaluation = false,
  canEditContent = false,
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['v1-panel-transcript', panelId],
    queryFn: () => v1Get(`/panels/${panelId}/transcript`),
    enabled: !!panelId,
    retry: false,
    refetchInterval: awaitingTranscript ? 5000 : false,
  });

  const startEdit = () => {
    setDraft(data?.text || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(data?.text || '');
    setEditing(false);
  };

  const saveEdit = async () => {
    if (!candidateId) return;
    setSaving(true);
    try {
      await v1Patch(`/candidates/${candidateId}/panels/${panelId}/content`, {
        transcript_text: draft,
      });
      toast.success('Transcript saved');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['v1-panel-transcript', panelId] });
      qc.invalidateQueries({ queryKey: ['v1-panel', panelId] });
      qc.invalidateQueries({ queryKey: ['v1-client-report-preview', candidateId] });
    } catch (e) {
      toastMutationError(e, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return <PanelTranscriptCard isLoading />;
  }
  if (editing && canEditContent && canEditEvaluation) {
    return (
      <div className="rounded-xl border border-border/60 bg-secondary/40 p-4 space-y-3" data-testid="panel-transcript-edit">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          <p className="text-sm font-semibold text-foreground">Transcript</p>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={saving} data-testid="panel-transcript-edit-cancel">
              Cancel
            </Button>
            <Button size="sm" onClick={saveEdit} disabled={saving} data-testid="panel-transcript-edit-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={14}
          className="text-xs font-mono"
          data-testid="panel-transcript-edit-textarea"
        />
      </div>
    );
  }
  if (data?.text) {
    return (
      <div className="space-y-2">
        {canEditContent && canEditEvaluation && (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={startEdit} data-testid="panel-transcript-edit-btn">
              <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
            </Button>
          </div>
        )}
        <PanelTranscriptCard text={data.text} charCount={data.char_count} />
      </div>
    );
  }
  if (awaitingTranscript) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="panel-transcript-generating">
        Generating transcript from recording…
      </p>
    );
  }
  if (lastError) {
    return (
      <PanelTranscriptCard
        errorMessage="Transcript generation failed. Re-upload with a transcript file, or try again later."
      />
    );
  }
  if (isError) {
    const detail = error?.response?.data?.detail;
    const msg = typeof detail === 'string' ? detail : detail?.message || 'Transcript not yet available.';
    return <PanelTranscriptCard errorMessage={msg} />;
  }
  return (
    <div className="space-y-2">
      {canEditContent && canEditEvaluation && (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={startEdit} data-testid="panel-transcript-edit-btn">
            <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
          </Button>
        </div>
      )}
      <PanelTranscriptCard text={data?.text} charCount={data?.char_count} />
    </div>
  );
}

function PanelEvaluationCard({
  panelId,
  candidateId,
  latestEvaluation,
  aiEvalStatus,
  aiEvalStartedAt,
  canEditEvaluation = false,
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draftSummary, setDraftSummary] = useState('');
  const [saving, setSaving] = useState(false);
  const mutation = useMutation({
    mutationFn: () => v1Post(`/panels/${panelId}/evaluate`, {}),
    onMutate: () => {
      qc.setQueryData(['v1-panel', panelId], (old) => {
        if (!old) return old;
        return { ...old, ai_eval_status: 'pending', ai_eval_started_at: null };
      });
    },
    onSuccess: () => {
      toast.success('AI evaluation generated.');
      qc.invalidateQueries({ queryKey: ['v1-panel', panelId] });
    },
    onError: (err) => {
      if (isPanelEvalAlreadyInProgressError(err)) {
        toast.info('AI evaluation already in progress.');
      } else {
        toast.error(GENERIC_EVAL_FAILURE_MSG);
      }
      qc.invalidateQueries({ queryKey: ['v1-panel', panelId] });
    },
  });

  const ev = latestEvaluation;
  const { inFlight, failed } = resolvePanelEvalUiState({
    aiEvalStatus,
    aiEvalStartedAt,
    isPending: mutation.isPending,
  });
  const dimensions = normalizePanelDimensions(ev?.payload_json?.dimensions);
  const strengths = normalizePanelStringList(ev?.payload_json?.strengths);
  const risks = normalizePanelStringList(ev?.payload_json?.risks);
  const buttonLabel = inFlight
    ? 'Evaluating…'
    : failed
      ? 'Retry evaluation'
      : ev
        ? 'Re-evaluate'
        : 'Run Evaluation';

  const startEdit = () => {
    setDraftSummary(ev?.payload_json?.summary || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraftSummary(ev?.payload_json?.summary || '');
    setEditing(false);
  };

  const saveEdit = async () => {
    if (!candidateId) return;
    setSaving(true);
    try {
      await v1Patch(`/candidates/${candidateId}/panels/${panelId}/content`, {
        eval_summary: draftSummary,
      });
      toast.success('AI summary saved');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['v1-panel', panelId] });
      qc.invalidateQueries({ queryKey: ['v1-client-report-preview', candidateId] });
    } catch (e) {
      toastMutationError(e, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border bg-card p-4" data-testid="panel-eval-card">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold">AI Evaluation</p>
          {failed && (
            <Badge className="ml-auto" variant="destructive" data-testid="panel-eval-failed-badge">
              Failed
            </Badge>
          )}
          {inFlight && (
            <Badge className="ml-auto" variant="secondary" data-testid="panel-eval-in-progress-badge">
              In progress
            </Badge>
          )}
          {ev && !failed && !inFlight && (
            <Badge className="ml-auto" variant="default">
              {panelRecommendationLabel(ev.recommendation)}
            </Badge>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {canEditEvaluation && ev && !failed && !inFlight && !editing && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={startEdit}
              data-testid="panel-eval-edit-btn"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
          )}
          {canEditEvaluation && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => { if (!inFlight && !editing) mutation.mutate(); }}
              disabled={inFlight || editing}
              data-testid="panel-eval-run-button"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${inFlight ? 'animate-spin' : ''}`} />
              {buttonLabel}
            </Button>
          )}
        </div>
      </div>

      {inFlight ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="panel-eval-in-progress">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>AI evaluation in progress…</span>
        </div>
      ) : failed ? (
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="panel-eval-failed">
          <p className="text-xs text-rose-600">{GENERIC_EVAL_FAILURE_MSG}</p>
        </div>
      ) : ev ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="text-3xl font-semibold text-primary">{(ev.overall_score ?? 0).toFixed(1)}</div>
            <div className="text-xs text-muted-foreground">/ 10 overall</div>
          </div>
          {editing ? (
            <div className="space-y-2" data-testid="panel-eval-edit-form">
              <p className="text-xs font-semibold text-foreground">AI summary</p>
              <Textarea
                value={draftSummary}
                onChange={(e) => setDraftSummary(e.target.value)}
                rows={5}
                className="text-sm"
                data-testid="panel-eval-edit-summary"
              />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={saving} data-testid="panel-eval-edit-cancel">
                  Cancel
                </Button>
                <Button size="sm" onClick={saveEdit} disabled={saving} data-testid="panel-eval-edit-save">
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm">{ev.payload_json?.summary}</p>
          )}
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {dimensions.map((d) => (
              <div key={d.name} className="rounded-lg border bg-secondary/40 p-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{String(d.name || '').replace(/_/g, ' ')}</div>
                <div className="text-lg font-semibold">{Number(d.score).toFixed(1)}</div>
              </div>
            ))}
          </div>
          {strengths.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-foreground/80">Strengths</p>
              <ul className="ml-4 list-disc text-xs text-muted-foreground">
                {strengths.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          {risks.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-foreground/80">Risks</p>
              <ul className="ml-4 list-disc text-xs text-muted-foreground">
                {risks.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          No evaluation yet. Run after the transcript is available.
        </p>
      )}
    </div>
  );
}

function SendBriefingDialog({ open, onOpenChange, panelId, candidateId, panelKind }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['v1-panel-briefing-preview', panelId],
    queryFn: () => v1Get(`/panels/${panelId}/briefing-preview`),
    enabled: open && !!panelId,
    retry: false,
  });

  const [guidelines, setGuidelines] = useState('');
  const [questions, setQuestions] = useState(['']);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!open) {
      setHydrated(false);
      return;
    }
    if (data && !hydrated) {
      setGuidelines(data.guidelines || '');
      setQuestions(
        (data.questions || []).length > 0 ? data.questions.map(String) : [''],
      );
      setHydrated(true);
    }
  }, [open, data, hydrated]);

  const sendMut = useMutation({
    mutationFn: () => v1Post(`/panels/${panelId}/send-briefing`, {
      guidelines: guidelines.trim(),
      questions: questions.map((q) => q.trim()).filter(Boolean),
    }),
    onSuccess: (res) => {
      const sent = res?.sent ?? 0;
      const failed = res?.failed ?? 0;
      if (res?.email_dispatch && res.email_dispatch.status !== 'sent') {
        toastEmailDispatch(res.email_dispatch);
      } else if (failed > 0) {
        toast.warning(`Briefing sent to ${sent} panellist(s); ${failed} failed.`);
      } else {
        toast.success(`Briefing emailed to ${sent} panellist(s).`);
      }
      qc.invalidateQueries({ queryKey: ['v1-panels', candidateId, panelKind] });
      qc.invalidateQueries({ queryKey: ['v1-panel', panelId] });
      onOpenChange(false);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      const msg =
        (typeof detail === 'string' ? detail : detail?.message) ||
        'Could not send panellist briefing.';
      toast.error(msg);
    },
  });

  const canSend = guidelines.trim().length > 0 && !sendMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="panel-briefing-dialog">
        <DialogHeader>
          <DialogTitle>Send panellist briefing</DialogTitle>
          <DialogDescription>
            HireIQ email to panellists only (not the candidate). Edit questions and guidelines before sending.
            Calendar invites are unchanged.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <p className="text-xs text-muted-foreground">Loading briefing preview…</p>
        )}
        {isError && (
          <div className="space-y-2">
            <p className="text-xs text-destructive">
              {typeof error?.response?.data?.detail === 'string'
                ? error.response.data.detail
                : 'Could not load briefing preview.'}
            </p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
          </div>
        )}

        {data && (
          <div className="space-y-4">
            <div className="rounded-lg border bg-secondary/30 p-3 text-xs space-y-1" data-testid="panel-briefing-summary">
              <p><span className="font-semibold">Round:</span> {data.round_label}</p>
              <p><span className="font-semibold">Candidate:</span> {data.candidate_name}</p>
              <p><span className="font-semibold">Role:</span> {data.role_label}</p>
              <p>
                <span className="font-semibold">When:</span>{' '}
                {formatDateRange(data.start_at, data.end_at)} ({data.timezone_label})
              </p>
              {data.join_url ? (
                <p className="break-all">
                  <span className="font-semibold">Teams:</span>{' '}
                  <a href={data.join_url} target="_blank" rel="noreferrer" className="text-primary underline">
                    {data.join_url}
                  </a>
                </p>
              ) : (
                <p className="text-amber-700">Teams join link not available yet.</p>
              )}
              <div className="flex flex-wrap gap-1.5 pt-1">
                <span className="font-semibold self-center">To:</span>
                {(data.panel_emails || []).map((email) => (
                  <Badge key={email} variant="secondary">{email}</Badge>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">Guidelines for panellists</label>
              <Textarea
                rows={5}
                value={guidelines}
                onChange={(e) => setGuidelines(e.target.value)}
                placeholder="e.g. Focus on behavioural signals; leave 10 minutes for candidate Q&A…"
                data-testid="panel-briefing-guidelines"
                className="mt-1"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground">Question checklist (editable)</label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setQuestions((prev) => [...prev, ''])}
                  data-testid="panel-briefing-add-question"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add question
                </Button>
              </div>
              {questions.map((q, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="mt-2 text-xs text-muted-foreground w-5 tabular-nums">{i + 1}.</span>
                  <Textarea
                    rows={2}
                    value={q}
                    onChange={(e) => {
                      const next = [...questions];
                      next[i] = e.target.value;
                      setQuestions(next);
                    }}
                    data-testid={`panel-briefing-question-${i}`}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-rose-600"
                    disabled={questions.length <= 1}
                    onClick={() => setQuestions((prev) => prev.filter((_, idx) => idx !== i))}
                    data-testid={`panel-briefing-remove-question-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => sendMut.mutate()}
            disabled={!data || !canSend}
            className="gap-1.5"
            data-testid="panel-briefing-send"
          >
            <Mail className="h-4 w-4" />
            {sendMut.isPending ? 'Sending…' : 'Send to panellists'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SendFeedbackDialog({ open, onOpenChange, panelId, candidateId, panelKind }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['v1-panel-feedback-preview', panelId],
    queryFn: () => v1Get(`/panels/${panelId}/feedback-preview`),
    enabled: open && !!panelId,
    retry: false,
  });

  const [categories, setCategories] = useState(['']);
  const [questions, setQuestions] = useState(['']);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!open) {
      setHydrated(false);
      return;
    }
    if (data && !hydrated) {
      setCategories((data.categories || []).length > 0 ? data.categories.map(String) : ['']);
      setQuestions((data.questions || []).length > 0 ? data.questions.map(String) : ['']);
      setHydrated(true);
    }
  }, [open, data, hydrated]);

  const cleanCategories = categories.map((c) => c.trim()).filter(Boolean);

  const sendMut = useMutation({
    mutationFn: () => v1Post(`/panels/${panelId}/send-feedback`, {
      categories: cleanCategories,
      questions: questions.map((q) => q.trim()).filter(Boolean),
    }),
    onSuccess: (res) => {
      const sent = res?.sent ?? 0;
      const failed = res?.failed ?? 0;
      if (res?.email_dispatch && res.email_dispatch.status !== 'sent') {
        toastEmailDispatch(res.email_dispatch);
      } else if (failed > 0) {
        toast.warning(`Feedback link sent to ${sent} panellist(s); ${failed} failed.`);
      } else {
        toast.success(`Feedback link emailed to ${sent} panellist(s).`);
      }
      qc.invalidateQueries({ queryKey: ['v1-panel-feedback', panelId] });
      qc.invalidateQueries({ queryKey: ['v1-panels', candidateId, panelKind] });
      if (panelKind === 'hr') {
        qc.invalidateQueries({ queryKey: ['v1-candidate', candidateId] });
        qc.invalidateQueries({ queryKey: ['hr-evaluation-ratings', candidateId] });
        qc.invalidateQueries({ queryKey: ['v1-artefact-readiness', candidateId] });
      }
      onOpenChange(false);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      const msg =
        (typeof detail === 'string' ? detail : detail?.message) ||
        'Could not send feedback links.';
      toast.error(msg);
    },
  });

  const canSend = cleanCategories.length > 0 && !sendMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="panel-feedback-dialog">
        <DialogHeader>
          <DialogTitle>Send panellist feedback form</DialogTitle>
          <DialogDescription>
            Each panellist gets a private, single-use link to rate the categories (1–5) and add
            a note. Ratings are averaged once everyone submits. Notes stay staff-only.
          </DialogDescription>
        </DialogHeader>

        {isLoading && <p className="text-xs text-muted-foreground">Loading feedback preview…</p>}
        {isError && (
          <div className="space-y-2">
            <p className="text-xs text-destructive">
              {typeof error?.response?.data?.detail === 'string'
                ? error.response.data.detail
                : 'Could not load feedback preview.'}
            </p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
          </div>
        )}

        {data && (
          <div className="space-y-4">
            <div className="rounded-lg border bg-secondary/30 p-3 text-xs space-y-1" data-testid="panel-feedback-summary">
              <p><span className="font-semibold">Round:</span> {data.round_label}</p>
              <p><span className="font-semibold">Candidate:</span> {data.candidate_name}</p>
              <p><span className="font-semibold">Role:</span> {data.role_label}</p>
              <div className="flex flex-wrap gap-1.5 pt-1">
                <span className="font-semibold self-center">To:</span>
                {(data.panel_emails || []).map((email) => (
                  <Badge key={email} variant="secondary">{email}</Badge>
                ))}
              </div>
              {data.has_active_request && (
                <p className="text-amber-700 pt-1">
                  Resending replaces the previous feedback links (old links stop working).
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground">
                  Scoring categories (editable, 1–12)
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={categories.length >= 12}
                  onClick={() => setCategories((prev) => [...prev, ''])}
                  data-testid="panel-feedback-add-category"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add category
                </Button>
              </div>
              {categories.map((c, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className="text-xs text-muted-foreground w-5 tabular-nums">{i + 1}.</span>
                  <Input
                    value={c}
                    onChange={(e) => {
                      const next = [...categories];
                      next[i] = e.target.value;
                      setCategories(next);
                    }}
                    data-testid={`panel-feedback-category-${i}`}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-rose-600"
                    disabled={categories.length <= 1}
                    onClick={() => setCategories((prev) => prev.filter((_, idx) => idx !== i))}
                    data-testid={`panel-feedback-remove-category-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground">Questions asked (editable)</label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setQuestions((prev) => [...prev, ''])}
                  data-testid="panel-feedback-add-question"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add question
                </Button>
              </div>
              {questions.map((q, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="mt-2 text-xs text-muted-foreground w-5 tabular-nums">{i + 1}.</span>
                  <Textarea
                    rows={2}
                    value={q}
                    onChange={(e) => {
                      const next = [...questions];
                      next[i] = e.target.value;
                      setQuestions(next);
                    }}
                    data-testid={`panel-feedback-question-${i}`}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-rose-600"
                    disabled={questions.length <= 1}
                    onClick={() => setQuestions((prev) => prev.filter((_, idx) => idx !== i))}
                    data-testid={`panel-feedback-remove-question-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => sendMut.mutate()}
            disabled={!data || !canSend}
            className="gap-1.5"
            data-testid="panel-feedback-send"
          >
            <ClipboardList className="h-4 w-4" />
            {sendMut.isPending ? 'Sending…' : 'Send to panellists'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FB_STATUS_LABEL = { sent: 'Sent', opened: 'Opened', submitted: 'Submitted', awaiting: 'Awaiting' };

function FeedbackMatrixTable({ matrix }) {
  const cols = matrix?.panellists || [];
  const cats = matrix?.categories || [];
  const aggByLabel = Object.fromEntries(((matrix?.aggregates) || []).map((a) => [a.label, a.mean]));

  if (!matrix || matrix.total_count === 0) {
    return (
      <div className="rounded-xl border bg-card p-4 space-y-2" data-testid="panel-feedback-empty">
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold">Evaluation</p>
        </div>
        <p className="text-xs text-muted-foreground">
          Please send the feedback form to the panellist(s) for rating. Ratings will appear
          here as each panellist submits.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="panel-feedback-matrix">
      <div className="flex items-center gap-2">
        <Star className="h-4 w-4 text-primary" />
        <p className="text-sm font-semibold">Evaluation</p>
        <Badge variant="outline" className="ml-auto text-xs">
          {matrix.submitted_count}/{matrix.total_count} submitted
        </Badge>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b">
              <th className="py-2 pr-3 text-left font-medium text-muted-foreground">Category</th>
              {cols.map((c) => (
                <th key={c.email} className="px-2 py-2 text-center font-medium" title={c.email}>
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold">
                      {c.initials}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {FB_STATUS_LABEL[c.status] || FB_STATUS_LABEL.awaiting}
                    </span>
                  </div>
                </th>
              ))}
              <th className="px-2 py-2 text-center font-medium text-muted-foreground">Avg</th>
            </tr>
          </thead>
          <tbody>
            {cats.map((cat) => (
              <tr key={cat} className="border-b last:border-0">
                <td className="py-2 pr-3 text-left">{cat}</td>
                {cols.map((c) => (
                  <td key={c.email + cat} className="px-2 py-2 text-center tabular-nums">
                    {c.status === 'submitted' && c.scores?.[cat] != null
                      ? Number(c.scores[cat]).toFixed(1)
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                ))}
                <td className="px-2 py-2 text-center font-semibold tabular-nums">
                  {aggByLabel[cat] != null ? Number(aggByLabel[cat]).toFixed(2) : '—'}
                </td>
              </tr>
            ))}
            <tr className="border-t">
              <td className="py-2 pr-3 text-left font-semibold">Overall</td>
              {cols.map((c) => {
                const vals = Object.values(c.scores || {});
                const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
                return (
                  <td key={`ov-${c.email}`} className="px-2 py-2 text-center font-semibold tabular-nums">
                    {c.status === 'submitted' && avg != null ? avg.toFixed(1) : '—'}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-center font-semibold tabular-nums text-primary">
                {matrix.overall_mean != null ? Number(matrix.overall_mean).toFixed(2) : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {cols.some((c) => c.note) && (
        <div className="space-y-2 pt-1">
          <p className="text-xs font-semibold text-foreground/80">Feedback notes (staff only)</p>
          {cols.filter((c) => c.note).map((c) => (
            <div key={`note-${c.email}`} className="rounded-md border bg-secondary/30 p-2 text-xs">
              <span className="font-semibold">{c.initials}</span>
              <span className="text-muted-foreground"> · {c.email}</span>
              <p className="mt-1 whitespace-pre-wrap">{c.note}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PanelInterviewCard({ panel, candidateId, panelKind, copy, roundLabel = null, canEditEvaluation = false }) {
  const qc = useQueryClient();
  const showAi = panelKind === 'tech';
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  // PipelineTimeline reads panels from candidate hydrate — keep it in sync when
  // panel status changes (schedule lives in SchedulePanelDialog; cancel/sync here).
  const invalidateCandidateStepper = () => {
    qc.invalidateQueries({ queryKey: ['v1-candidate', candidateId] });
    qc.invalidateQueries({ queryKey: ['v1-candidate-activity', candidateId] });
    if (panelKind === 'hr') {
      qc.invalidateQueries({ queryKey: ['hr-evaluation-ratings', candidateId] });
      qc.invalidateQueries({ queryKey: ['v1-artefact-readiness', candidateId] });
    }
  };

  const { data: detail } = useQuery({
    queryKey: ['v1-panel', panel.id],
    queryFn: () => v1Get(`/panels/${panel.id}`),
    enabled: !!panel.id,
    refetchInterval: (q) => {
      const data = q.state.data;
      const evalStatus = data?.ai_eval_status;
      const evalRunning = evalStatus === 'pending'
        || (evalStatus === 'in_progress' && !isEvalUiStale(data?.ai_eval_started_at));
      if (evalRunning) return 4000;
      const st = data?.status || panel.status;
      return st === 'artifacts_ready' ? 5000 : false;
    },
  });

  useEffect(() => {
    if (detail?.status === 'evaluated' && panel.status !== 'evaluated') {
      qc.invalidateQueries({ queryKey: ['v1-panels', candidateId, panelKind] });
      qc.invalidateQueries({ queryKey: ['v1-candidate', candidateId] });
      qc.invalidateQueries({ queryKey: ['v1-candidate-activity', candidateId] });
    }
  }, [detail?.status, panel.status, candidateId, panelKind, qc]);
  const hasTranscriptArtifact = (detail?.artifacts || []).some((a) => a.kind === 'transcript');
  useEffect(() => {
    if (hasTranscriptArtifact) {
      qc.invalidateQueries({ queryKey: ['v1-panel-transcript', panel.id] });
    }
  }, [hasTranscriptArtifact, panel.id, qc]);
  const { data: feedbackMatrix } = useQuery({
    queryKey: ['v1-panel-feedback', panel.id],
    queryFn: () => v1Get(`/panels/${panel.id}/feedback`),
    enabled: !!panel.id,
    refetchInterval: 30000,
  });
  useEffect(() => {
    if (panelKind === 'hr' && feedbackMatrix?.all_submitted) {
      qc.invalidateQueries({ queryKey: ['v1-candidate', candidateId] });
      qc.invalidateQueries({ queryKey: ['hr-evaluation-ratings', candidateId] });
      qc.invalidateQueries({ queryKey: ['v1-artefact-readiness', candidateId] });
    }
  }, [feedbackMatrix?.all_submitted, panelKind, candidateId, qc]);

  const syncMut = useMutation({
    mutationFn: () => v1Post(`/panels/${panel.id}/sync`, {}),
    onSuccess: (data) => {
      toast.success(`Synced — ${data.recordings_indexed} recording(s), ${data.transcripts_indexed} transcript(s).`);
      qc.invalidateQueries({ queryKey: ['v1-panel', panel.id] });
      qc.invalidateQueries({ queryKey: ['v1-panels', candidateId, panelKind] });
      invalidateCandidateStepper();
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Sync failed.';
      toast.error(msg);
    },
  });

  const cancelMut = useMutation({
    mutationFn: () => v1Post(`/panels/${panel.id}/cancel`, {}),
    onSuccess: () => {
      toast.success(copy.cancelToast);
      qc.invalidateQueries({ queryKey: ['v1-panel', panel.id] });
      qc.invalidateQueries({ queryKey: ['v1-panels', candidateId, panelKind] });
      invalidateCandidateStepper();
    },
  });

  const status = detail?.status || panel.status;
  const badge = STATUS_BADGE[status] || { variant: 'outline', label: status };
  const recordings = (detail?.artifacts || []).filter((a) => a.kind === 'recording');
  const hasTranscript = (detail?.artifacts || []).some((a) => a.kind === 'transcript');
  const lastError = detail?.last_error || panel.last_error || null;
  const isCancelled = status === 'cancelled';
  const awaitingTranscript = recordings.length > 0 && !hasTranscript && !lastError && !isCancelled;
  const mediaReadyStatus = status === 'artifacts_ready' || status === 'evaluated';
  const showArtifacts = recordings.length > 0 || hasTranscript || mediaReadyStatus;
  const isManual = !panel.graph_online_meeting_id && panel.auto_record === false;
  const defaultTab = recordings.length > 0 ? 'recording' : 'transcript';
  const briefingSent = !!(panel.briefing_sent_at || detail?.briefing_sent_at);
  const fbTotal = feedbackMatrix?.total_count || 0;
  const fbSubmitted = feedbackMatrix?.submitted_count || 0;
  const feedbackLabel = fbTotal === 0
    ? 'Send feedback form'
    : (fbSubmitted >= fbTotal ? 'Feedback complete' : `Feedback ${fbSubmitted}/${fbTotal}`);

  const hasAiEval = showAi && !!(detail?.latest_evaluation || detail?.ai_eval_status === 'completed');
  const hasArtifactsOrScoring = recordings.length > 0 || hasTranscript || hasAiEval || fbSubmitted > 0;
  const hasTeamsMeeting = !!(panel.graph_online_meeting_id || panel.graph_event_id);
  const cancelDialogTitle = roundLabel
    ? `Cancel ${roundLabel}?`
    : (panelKind === 'hr' ? 'Cancel HR Call?' : 'Cancel Panel Interview?');

  return (
    <Card className="card-elevated" data-testid={`panel-card-${panel.id}`}>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <Calendar className="mt-1 h-5 w-5 text-primary" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate text-base font-semibold">{panel.subject}</p>
              {roundLabel && (
                <Badge variant="secondary" data-testid={`panel-round-label-${panel.id}`}>{roundLabel}</Badge>
              )}
              {isManual && (
                <Badge variant="outline" data-testid={`panel-manual-uploaded-${panel.id}`}>
                  Manual Uploaded
                </Badge>
              )}
              {status !== 'evaluated' && (
                <Badge variant={badge.variant}>{badge.label}</Badge>
              )}
              {briefingSent && (
                <Badge variant="outline" data-testid={`panel-briefing-sent-${panel.id}`}>Briefing sent</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{formatDateRange(panel.start_at, panel.end_at)}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Panel: {(panel.panel_emails || panel.panel_emails_json || []).join(', ')}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
            {canEditEvaluation && !isCancelled && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setBriefingOpen(true)}
                data-testid={`panel-briefing-${panel.id}`}
              >
                <Mail className="h-3.5 w-3.5" />
                {briefingSent ? 'Resend briefing' : 'Send briefing'}
              </Button>
            )}
            {canEditEvaluation && !isCancelled && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setFeedbackOpen(true)}
                data-testid={`panel-feedback-${panel.id}`}
              >
                <ClipboardList className="h-3.5 w-3.5" />
                {feedbackLabel}
              </Button>
            )}
            {panel.join_url && (
              <a href={panel.join_url} target="_blank" rel="noreferrer" data-testid={`panel-join-${panel.id}`}>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <ExternalLink className="h-3.5 w-3.5" /> Join
                </Button>
              </a>
            )}
            {canEditEvaluation && !isManual && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => syncMut.mutate()}
                disabled={syncMut.isPending || isCancelled}
                data-testid={`panel-sync-${panel.id}`}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${syncMut.isPending ? 'animate-spin' : ''}`} /> Sync
              </Button>
            )}
            {canEditEvaluation && !isCancelled && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-rose-600 hover:bg-rose-50"
                onClick={() => setCancelConfirmOpen(true)}
                disabled={cancelMut.isPending}
                data-testid={`panel-cancel-${panel.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" /> Cancel
              </Button>
            )}
          </div>
        </div>

        {panel.last_error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
            {panel.last_error}
          </p>
        )}

        {showArtifacts && (
          <Tabs defaultValue={defaultTab}>
            <TabsList>
              <TabsTrigger value="recording" data-testid="panel-tab-recording">Recording</TabsTrigger>
              <TabsTrigger value="transcript" data-testid="panel-tab-transcript">Transcript</TabsTrigger>
              {showAi && (
                <TabsTrigger value="evaluation" data-testid="panel-tab-evaluation">AI Evaluation</TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="recording" className="space-y-3">
              {recordings.length === 0 ? (
                <p className="text-xs text-muted-foreground">No recording indexed yet — try Sync after the meeting.</p>
              ) : (
                recordings.map((a) => (
                  <PanelRecordingWidget key={a.id} panelId={panel.id} artifact={a} />
                ))
              )}
            </TabsContent>
            <TabsContent value="transcript">
              <PanelTranscriptPane
                panelId={panel.id}
                candidateId={candidateId}
                awaitingTranscript={awaitingTranscript}
                lastError={lastError}
                canEditEvaluation={canEditEvaluation}
                canEditContent={showAi}
              />
            </TabsContent>
            {showAi && (
              <TabsContent value="evaluation">
                <PanelEvaluationCard
                  panelId={panel.id}
                  candidateId={candidateId}
                  latestEvaluation={detail?.latest_evaluation}
                  aiEvalStatus={detail?.ai_eval_status}
                  aiEvalStartedAt={detail?.ai_eval_started_at}
                  canEditEvaluation={canEditEvaluation}
                />
              </TabsContent>
            )}
          </Tabs>
        )}

        <FeedbackMatrixTable matrix={feedbackMatrix} />

        <SendBriefingDialog
          open={briefingOpen}
          onOpenChange={setBriefingOpen}
          panelId={panel.id}
          candidateId={candidateId}
          panelKind={panelKind}
        />
        <SendFeedbackDialog
          open={feedbackOpen}
          onOpenChange={setFeedbackOpen}
          panelId={panel.id}
          candidateId={candidateId}
          panelKind={panelKind}
        />

        <AlertDialog
          open={cancelConfirmOpen}
          onOpenChange={(open) => {
            if (!cancelMut.isPending) {
              setCancelConfirmOpen(open);
            }
          }}
        >
          <AlertDialogContent data-testid={`panel-cancel-dialog-${panel.id}`}>
            <AlertDialogHeader>
              <div className="flex items-center gap-2.5">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${hasArtifactsOrScoring ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'
                    }`}
                >
                  {hasArtifactsOrScoring ? (
                    <AlertTriangle className="h-5 w-5" />
                  ) : (
                    <Trash2 className="h-5 w-5" />
                  )}
                </div>
                <AlertDialogTitle className="text-lg font-semibold">
                  {cancelDialogTitle}
                </AlertDialogTitle>
              </div>
              <AlertDialogDescription asChild>
                <div className="space-y-3 pt-2 text-sm text-muted-foreground">
                  {hasArtifactsOrScoring ? (
                    <>
                      <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 text-amber-900">
                        <p className="font-semibold text-xs text-amber-900">
                          Critical: Existing interview media &amp; scoring will be excluded
                        </p>
                        <p className="mt-1 text-xs text-amber-800 leading-relaxed">
                          This round already has recorded sessions, transcripts, or evaluation data. Cancelling this interview will permanently exclude and detach all of these artifacts from the candidate&apos;s stage progress and client report.
                        </p>
                      </div>

                      <div className="rounded-md border bg-muted/40 p-3 space-y-2 text-xs">
                        <p className="font-medium text-foreground">
                          The following data will be detached &amp; removed:
                        </p>
                        <ul className="space-y-1.5 text-muted-foreground">
                          {recordings.length > 0 && (
                            <li className="flex items-center gap-2 text-rose-700 font-medium">
                              <Video className="h-3.5 w-3.5 shrink-0" />
                              <span>{recordings.length} video recording{recordings.length > 1 ? 's' : ''}</span>
                            </li>
                          )}
                          {hasTranscript && (
                            <li className="flex items-center gap-2 text-rose-700 font-medium">
                              <FileText className="h-3.5 w-3.5 shrink-0" />
                              <span>Meeting transcript &amp; dialogue</span>
                            </li>
                          )}
                          {hasAiEval && (
                            <li className="flex items-center gap-2 text-rose-700 font-medium">
                              <Sparkles className="h-3.5 w-3.5 shrink-0" />
                              <span>
                                AI evaluation scoring
                                {detail?.latest_evaluation?.overall_score != null
                                  ? ` (${detail.latest_evaluation.overall_score}/100)`
                                  : ''}
                                {detail?.latest_evaluation?.verdict ? ` — verdict: ${detail.latest_evaluation.verdict}` : ''}
                              </span>
                            </li>
                          )}
                          {fbSubmitted > 0 && (
                            <li className="flex items-center gap-2 text-rose-700 font-medium">
                              <ClipboardList className="h-3.5 w-3.5 shrink-0" />
                              <span>{fbSubmitted} submitted panel feedback evaluation{fbSubmitted > 1 ? 's' : ''}</span>
                            </li>
                          )}
                          {hasTeamsMeeting && (
                            <li className="flex items-center gap-2 text-muted-foreground">
                              <Calendar className="h-3.5 w-3.5 shrink-0" />
                              <span>Microsoft Teams meeting will be cancelled from Outlook calendars</span>
                            </li>
                          )}
                        </ul>
                      </div>

                      <p className="text-xs font-medium text-destructive">
                        This action cannot be undone. To retain these artifacts, keep the interview active.
                      </p>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <p>
                        Are you sure you want to cancel this {panelKind === 'hr' ? 'HR call' : 'panel interview'} for <strong className="text-foreground">{panel.subject}</strong>?
                      </p>
                      {hasTeamsMeeting ? (
                        <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground flex items-start gap-2">
                          <Calendar className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                          <span>The Microsoft Teams calendar event will be removed from Outlook, and the meeting link will become invalid.</span>
                        </div>
                      ) : (
                        <p className="text-xs">
                          The slot will be vacated, allowing a new interview to be scheduled or manually uploaded.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-2 gap-2 sm:gap-0">
              <AlertDialogCancel
                disabled={cancelMut.isPending}
                data-testid={`panel-cancel-dismiss-${panel.id}`}
              >
                Keep Interview
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  cancelMut.mutate();
                }}
                disabled={cancelMut.isPending}
                className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5 focus:ring-rose-600"
                data-testid={`panel-cancel-confirm-${panel.id}`}
              >
                {cancelMut.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Cancelling…
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Cancel Interview
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
